import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import SharedButton from '../../components/Shared/SharedButton';
import SearchField from '../../components/Shared/SearchField';
import DropdownComponent from '../../components/Shared/DropdownComponent';
import CustomTable from '../../components/Shared/CustomTable';
import CustomPagination from '../../components/Shared/CustomPagination';
import TableLoading from '../../components/Shared/TableLoading';
import { EditIcon, EyeIcon, CancelIcon, PrintIcon } from '../../components/Shared/TableActionIcons';
import { getERPClient } from '../../services/supplyChainApi';
import { openPrintWindow, buildPurchaseInvoicePrintHtml, erpBusinessToBiz } from '../../helpers/invoicePrint';
import toast from 'react-hot-toast';

export default function ERPPurchaseInvoicesMain() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const business = auth?.user?.Business;

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelInvoiceId, setCancelInvoiceId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [printingId, setPrintingId] = useState(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const erpClient = getERPClient();
      const params = {
        q: search.trim() || undefined,
        limit: 100,
        offset: 0,
      };
      if (statusFilter !== 'ALL') params.status = statusFilter;

      const res = await erpClient.getPurchaseInvoices(params);
      const list = Array.isArray(res) ? res : (res?.data || res?.purchase_invoices || res?.invoices || []);
      setInvoices(list);
    } catch (error) {
      console.error('Failed to fetch purchase invoices:', error);
      toast.error(t('Failed to load purchase invoices'));
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, t]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handlePrint = async (row) => {
    setPrintingId(row.id);
    try {
      const erpClient = getERPClient();
      const res = await erpClient.getPurchaseInvoice(row.id);
      const inv = res.data || res;
      if (!inv || !inv.invoice_number) throw new Error('no invoice');
      openPrintWindow(buildPurchaseInvoicePrintHtml(inv, erpBusinessToBiz(business)), `Purchase Invoice ${inv.invoice_number}`);
    } catch (err) {
      console.error('Failed to print purchase invoice:', err);
      toast.error(t('Failed to print invoice'));
    } finally {
      setPrintingId(null);
    }
  };

  const handleOpenCancel = (invoice) => {
    setCancelInvoiceId(invoice.id || invoice.invoice_number);
    setCancelReason('');
    setCancelModalVisible(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error(t('Please specify a cancellation reason'));
      return;
    }
    setCancelling(true);
    try {
      const erpClient = getERPClient();
      await erpClient.cancelPurchaseInvoice(cancelInvoiceId, cancelReason);
      toast.success(t('Purchase Invoice cancelled'));
      setCancelModalVisible(false);
      fetchInvoices();
    } catch (err) {
      console.error('Failed to cancel invoice:', err);
      const msg = err.response?.data?.message || t('Failed to cancel invoice');
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  const statusBodyTemplate = (rowData) => {
    const status = rowData.status || 'ISSUED';
    const severity = status === 'CANCELLED' || status === 'REFUNDED' ? 'danger' : status === 'PAID' ? 'success' : 'info';
    return <Tag value={status} severity={severity} />;
  };

  const actionBodyTemplate = (rowData) => {
    const isClosed = rowData.status === 'CANCELLED' || rowData.status === 'REFUNDED';
    return (
      <div className="flex items-center gap-2">
        <EyeIcon
          onClick={() => {
            setSelectedInvoice(rowData);
            setDetailVisible(true);
          }}
          title={t('View Details')}
        />
        {printingId === rowData.id ? (
          <i className="pi pi-spin pi-spinner text-primary" />
        ) : (
          <PrintIcon onClick={() => handlePrint(rowData)} title={t('Print')} />
        )}
        {!isClosed && (
          <EditIcon
            onClick={() => navigate(`/manager/erp/purchase-invoices/edit/${rowData.id}`)}
            title={t('Edit')}
          />
        )}
        {!isClosed && (
          <CancelIcon
            onClick={() => handleOpenCancel(rowData)}
            title={t('Cancel Invoice')}
          />
        )}
      </div>
    );
  };

  const sequenceBodyTemplate = (rowData, options) => {
    return options.rowIndex + 1 + (page - 1) * perPage;
  };

  const statusOptions = [
    { label: t('All Invoices'), value: 'ALL' },
    { label: t('Received'), value: 'RECEIVED' },
    { label: t('Pending'), value: 'PENDING' },
    { label: t('Draft'), value: 'DRAFT' },
    { label: t('Confirmed'), value: 'CONFIRMED' },
    { label: t('Cancelled'), value: 'CANCELLED' },
  ];

  const columnsData = [
    { body: actionBodyTemplate, header: t('actions'), style: { width: '120px' } },
    { body: sequenceBodyTemplate, header: t('no'), style: { width: '3rem' } },
    { field: 'invoice_number', header: t('Invoice #'), style: { width: '150px' } },
    { body: (row) => row.supplier_name || row.supplier_name_ar || row.supplier_id || '-', field: 'supplier_name', header: t('Supplier') },
    { body: (row) => row.invoice_date || '-', field: 'invoice_date', header: t('Invoice Date'), style: { width: '120px' } },
    { body: (row) => row.payment_due_date || '-', field: 'payment_due_date', header: t('Payment Deadline'), style: { width: '130px' } },
    { body: (row) => `SAR ${Number(row.total_amount || row.grand_total || 0).toFixed(2)}`, field: 'total_amount', header: t('Final Total'), style: { width: '130px' } },
    { body: statusBodyTemplate, field: 'status', header: t('Status'), style: { width: '110px' } },
  ];

  const paginatedData = invoices.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(invoices.length / perPage) || 1;

  const handlePageChange = (newPage, newPerPage) => {
    setPage(newPage);
    if (newPerPage) setPerPage(newPerPage);
  };

  return (
    <div>
      <div className="flex flex-col mb-2 md:flex-row justify-between items-center gap-3">
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchField
            className="w-full md:w-[320px]"
            placeholder={t('Search by Invoice #, Supplier...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            handleClearClick={() => setSearch('')}
          />
          <div className="w-full sm:w-[200px]">
            <DropdownComponent
              useFormik={false}
              value={statusFilter}
              handleChange={(e) => setStatusFilter(e.value)}
              options={statusOptions}
              placeholder={t('Status Filter')}
            />
          </div>
        </div>

        <SharedButton
          label={t('Create Purchase Invoice')}
          icon="pi pi-plus"
          onClick={() => navigate('/manager/erp/purchase-invoices/create')}
          buttonType="primary"
        />
      </div>

      <CustomTable
        data={paginatedData}
        isLoading={loading}
        loadingIcon={<TableLoading />}
        columns={columnsData}
        rows={perPage}
        rowClassName={(row) =>
          row?.status === 'CANCELLED' || row?.status === 'REFUNDED'
            ? 'bg-red-50'
            : ''
        }
      />

      {invoices.length > 0 && (
        <CustomPagination
          currentPage={page}
          rowsPerPage={perPage}
          totalRecords={totalPages}
          totalRecord={invoices.length}
          onPageChange={handlePageChange}
        />
      )}

      {/* View Detail Dialog */}
      <Dialog
        header={t('Purchase Invoice Details')}
        visible={detailVisible}
        onHide={() => setDetailVisible(false)}
        pt={{
          root: { className: 'w-full lg:w-8/12 mx-4' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-4' },
          closeButton: { className: 'hover:!bg-black/20 !outline-none !shadow-none' },
          closeButtonIcon: { className: '!text-white' },
        }}
      >
        {selectedInvoice && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-gray-50 p-3 rounded border">
              <div>
                <span className="text-xs text-gray-500 block">{t('Invoice #')}</span>
                <span className="font-semibold">{selectedInvoice.invoice_number}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Supplier')}</span>
                <span className="font-semibold">{selectedInvoice.supplier_name || selectedInvoice.supplier_name_ar || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Invoice Date')}</span>
                <span>{(selectedInvoice.invoice_date || '-').split('T')[0]}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Payment Deadline')}</span>
                <span>{(selectedInvoice.payment_due_date || '-').split('T')[0]}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 block">{t('Status')}</span>
                {statusBodyTemplate(selectedInvoice)}
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">{t('Invoice Line Items')}</h4>
              <table className="w-full text-left text-xs border">
                <thead>
                  <tr className="bg-gray-100 border-b">
                    <th className="p-2">{t('Item Code')}</th>
                    <th className="p-2">{t('Item')}</th>
                    <th className="p-2">{t('Qty')}</th>
                    <th className="p-2">{t('UOM')}</th>
                    <th className="p-2">{t('Unit Price')}</th>
                    <th className="p-2">{t('VAT %')}</th>
                    <th className="p-2">{t('Total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedInvoice.lines || selectedInvoice.items || []).map((item, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2 font-medium">{item.item_code || '-'}</td>
                      <td className="p-2">{item.item_name || item.description || '-'}</td>
                      <td className="p-2">{item.quantity ?? item.qty ?? '-'}</td>
                      <td className="p-2">{item.uom_code || item.uom || item.uom_id || '-'}</td>
                      <td className="p-2">SAR {Number(item.unit_price || 0).toFixed(2)}</td>
                      <td className="p-2">{item.vat_pct ?? item.vat_rate ?? 0}%</td>
                      <td className="p-2 font-semibold">SAR {Number(item.line_total || item.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end border-t pt-3">
              <div className="text-right space-y-1">
                <div className="text-base font-bold text-primary mb-1">
                  {t('Final Total:')} SAR {Number(selectedInvoice.total_amount || selectedInvoice.grand_total || 0).toFixed(2)}
                </div>
                {selectedInvoice.currency && <div className="text-xs text-gray-500">{t('Currency:')} {selectedInvoice.currency}</div>}
              </div>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        header={t('Cancel Purchase Invoice')}
        visible={cancelModalVisible}
        onHide={() => setCancelModalVisible(false)}
        pt={{
          root: { className: 'w-full max-w-md mx-4' },
          header: { className: '!p-2 !bg-darkBg !text-white' },
          content: { className: '!p-4' },
          closeButton: { className: 'hover:!bg-black/20 !outline-none !shadow-none' },
          closeButtonIcon: { className: '!text-white' },
        }}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            {t('Are you sure you want to cancel this purchase invoice? Please state the reason for accounting audit logs.')}
          </p>
          <textarea
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder={t('e.g. Duplicate entry uploaded by accounting')}
            className="w-full border border-gray-300 rounded p-2 text-sm focus:outline-none focus:border-red-500"
          />
          <div className="flex justify-end gap-2 pt-2">
            <SharedButton
              type="button"
              label={t('Dismiss')}
              buttonType="reset"
              onClick={() => setCancelModalVisible(false)}
            />
            <SharedButton
              type="button"
              label={t('Confirm Cancellation')}
              buttonType="danger"
              loading={cancelling}
              onClick={handleConfirmCancel}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
